import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import { selectPlannerHorizonMeals } from '../redux/mealPlanSlice';
import { selectCurrentLanguage } from '../redux/languageSlice';
import { translateMealPlanRecipes } from '../services/mealPlanTranslation';
import { pendingTranslationKey } from '../utils/recipeLanguage';
import { buildTranslationActions } from './mealPlanTranslationActions';

/**
 * Hook that auto-translates meal plan recipes when:
 * 1. The app language changes
 * 2. Meals that don't match the current language enter the plan — at mount, and
 *    at any point after it
 * 3. Meals in the following Monday buckets the phone shows in one scroll
 *    (#722): `selectPlannerHorizonMeals`, not `selectCurrentWeekMeals`, or a
 *    dinner next week stays in its source language for the session (#737)
 *
 * (2) used to be a comment rather than a behaviour: the effect depended on
 * `[currentLanguage, dispatch]` and read meals from a ref, so it ran once at
 * mount and then only on a language switch. Meals written after mount were
 * never looked at again. `useGenerateWeek` — the "Re-generate" / "Generate My
 * Week" path, and what a new user lands on — commits its picks straight to
 * `mealPlanSlice` with no translation call of its own, so an English user got a
 * week of French recipe titles and a French grocery list that only fixed itself
 * on the next page load. `pendingTranslationKey` closes that gap.
 */
export function useMealPlanTranslation() {
  const dispatch = useDispatch<AppDispatch>();
  const currentLanguage = useSelector(selectCurrentLanguage);
  const currentWeekMeals = useSelector((state: RootState) => selectPlannerHorizonMeals(state));
  const mealsRef = useRef(currentWeekMeals);
  mealsRef.current = currentWeekMeals;

  const pendingKey = useMemo(
    () => pendingTranslationKey(currentWeekMeals, currentLanguage),
    [currentWeekMeals, currentLanguage],
  );

  const isTranslatingRef = useRef(false);
  // The last set we actually started a pass for. Re-attempting the identical
  // set is pointless and, on a recipe that fails to translate for a structural
  // reason, would retry forever — `passId` re-runs the effect after every pass,
  // and without this guard that is an unbounded loop against the API.
  const attemptedKeyRef = useRef<string | null>(null);
  const [passId, setPassId] = useState(0);

  useEffect(() => {
    if (!pendingKey) return;
    // A pass is already running. It bumps `passId` when it finishes, which
    // re-runs this effect against the set as it stands then — so a week
    // generated mid-pass is picked up rather than dropped.
    if (isTranslatingRef.current) return;
    if (attemptedKeyRef.current === pendingKey) return;

    const meals = mealsRef.current;

    const applyUpdates = (batch: Parameters<typeof buildTranslationActions>[1]) => {
      // Resolve against the CURRENT meals, not the ones captured when the pass
      // started — a late translation can land after the user has edited the
      // week, and mealsRef always holds the live set.
      for (const action of buildTranslationActions(mealsRef.current, batch)) {
        dispatch(action as Parameters<AppDispatch>[0]);
      }
    };

    attemptedKeyRef.current = pendingKey;
    isTranslatingRef.current = true;
    translateMealPlanRecipes(
      meals,
      currentLanguage,
      // Each background translation is applied the moment it lands, which is
      // what clears the grocery list's pending skeleton without a reload.
      update => applyUpdates([update]),
    ).then(applyUpdates).finally(() => {
      isTranslatingRef.current = false;
      setPassId(n => n + 1);
    });
  }, [pendingKey, passId, currentLanguage, dispatch]);
}
